import { useContext, useEffect } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { GlobalContext } from '../../../../library/Core/GlobalContext';
import { SmartContext } from '../../../../library/Core/SmartContext';
import smartReducer from '../../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../../library/Core/SmartTypes';

import DebugBarControl from '../../../../library/SimpleControls/DebugBarControl';
import RecruiterLayoutComponent from './RecruiterLayoutComponent';
import RecruiterDashboardAccordianItem from './RecruiterDashboardAccordianItem';

import RecruiterWorkingIndustry from './RecruiterWorkingIndustry';
import RecruiterIndustryRoles from './RecruiterIndustryRoles';
import RecruiterProgramsAvgCTC from './RecruiterProgramsAvgCTC';
import RecruiterCompanies from './RecruiterCompanies';
import RecruiterLocation from './RecruiterLocation';
import TypeOfRecruiters from './TypeOfRecruiters';
import PotentialRecruitersPie from './PotentialRecruitersPie';
import PotentialRecruitersBar from './PotentialRecruitersBar';

const RecruiterDashboard = () => {
    const { globalState } = useContext(GlobalContext);

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;

    useEffect(() => {
        dispatch({
            type: 'DATA_INIT',
            payload: { formConfig, data, domain: new Map([...globalState.domain]), internal, routeInfo },
        });
    }, []);

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            {!state.flags.isDataLoading && (
                <RecruiterLayoutComponent>
                    <div className="accordion dashboardAccordion" id="accordionPanelsStayOpenStudent">
                        <div className='row g-4' style={{paddingBottom: '30px'}}>
                            <div className='col-lg-6 col-md-6 col-sm-6'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'WORKING_INDUSTRY'}
                                        title="Working Industry"
                                        tooltip="Industry sectors in which recruiters are currently employed.">
                                        <RecruiterWorkingIndustry />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                            <div className='col-lg-6 col-md-6 col-sm-6'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'INDUSTRY_ROLES'}
                                        title="Industry Roles"
                                        tooltip="Job roles or functions recruiters are hiring for within their industries.">
                                        <RecruiterIndustryRoles />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                        </div>
                        <div className='row g-4' style={{paddingBottom: '30px'}}>
                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'PROGRAMS_AVG_CTC'}
                                        title="Programs Avg. CTC (in ₹)"
                                        tooltip="Average compensation offered by recruiters across different academic programs.">
                                        <RecruiterProgramsAvgCTC />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                        </div>
                        <div className='row g-4' style={{paddingBottom: '30px'}}>
                            <div className='col-lg-4 col-md-4 col-sm-4'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'COMPANIES'}
                                        title="Companies"
                                        tooltip="Distribution of recruiters based on company type or listing status.">
                                        <RecruiterCompanies />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                            <div className='col-lg-4 col-md-4 col-sm-4'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'RECRUITERS_LOCATION'}
                                        title="Recruiters Work Location"
                                        tooltip="Geographic distribution of recruiter job locations.">
                                        <RecruiterLocation />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                            <div className='col-lg-4 col-md-4 col-sm-4'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'TYPE_OF_RECRUITERS'}
                                        title="Type of Recruiters"
                                        tooltip="Classification of recruiters based on hiring scale or focus.">
                                        <TypeOfRecruiters />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                        </div>
                        <div className='row g-4'>
                            <div className="container max-1140 pt-sm-5 px-lg-0 overflow-hidden">
                                <h2 className="font-22 mb-3">
                                    Potential Recruiters
                                    <i className="bi bi-info-circle-fill defaultTooltip font-16 ms-2" data-bs-toggle= 'tooltip' title="Recruiters who have shown interest but have not yet confirmed hiring."></i>
                                </h2>
                            </div>
                            <div className='col-lg-6 col-md-6 col-sm-6'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'POTENTIAL_RECRUITERS'}
                                        title="Working Industry"
                                        tooltip="Industry sectors associated with potential recruiters.">
                                        <PotentialRecruitersPie />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                            <div className='col-lg-6 col-md-6 col-sm-6'>
                                {(
                                    <RecruiterDashboardAccordianItem
                                        reportKey={'POTENTIAL_RECRUITERS'}
                                        title="Intent Shown"
                                        tooltip="Level of hiring interest or intent expressed by potential recruiters.">
                                        <PotentialRecruitersBar />
                                    </RecruiterDashboardAccordianItem>
                                )}
                            </div>
                        </div>
                    </div>
                </RecruiterLayoutComponent>
            )}
            <DebugBarControl />
        </SmartContext.Provider>
    );
};

export default RecruiterDashboard;
